import { isEnabled } from './utils';
import { CHANNEL_ID } from './const';
import { DEFAULT_ACCOUNT_ID } from 'openclaw/plugin-sdk/account-id';

export const enabledDefault = true;
export const dmPolicyDefault = 'pairing';
export const groupPolicyDefault = 'allowlist';
export const requireMentionDefault = true;
export const emojiReactionDefault = true;
export const monitorEnabledDefault = true;

/** 格式化 allowFrom 配置项，确保其为小写字符串数组。 */
export const parseAllowFroms = (allowFrom: any): string[] => {
  if (!Array.isArray(allowFrom)) return [];
  return [...new Set(
    allowFrom
      .map((value: any) => value == null ? '' : String(value).toLowerCase().trim())
      .filter(Boolean),
  )];
};

const mergeArrs = (arr1: any, arr2: any) => {
  return [...new Set([...(arr1 || []), ...arr2 || []])];
};

export const getAccountInfo = (acct?: any) => ({
  enabled: isEnabled(acct?.enabled),
  appId: acct?.appId || '',
  appSecret: acct?.appSecret || undefined, // 避免默认值覆盖用户输入的空字符串 Secret 空字串系统也会认为是已填写
  dmPolicy: acct?.dmPolicy || dmPolicyDefault,
  allowFrom: parseAllowFroms(acct?.allowFrom || []),
  // 群组策略与白名单、群组级覆盖
  groupPolicy: acct?.groupPolicy || groupPolicyDefault,
  groupAllowFrom: parseAllowFroms(acct?.groupAllowFrom || []),
  requireMention: isEnabled(acct?.requireMention ?? requireMentionDefault),
  emojiReaction: isEnabled(acct?.emojiReaction ?? emojiReactionDefault),
  monitorEnabled: acct?.monitorEnabled ?? monitorEnabledDefault,
});

export const resolveAccount = (cfg: any, accountId?: string | null, agentId?: string | null) => {
  // If agentId is provided, try to resolve accountId from cfg.bindings first
  let resolvedAccountId = accountId;
  if (agentId) {
    const bindings: any[] = cfg?.bindings || [];
    const matched = bindings.find(
      (b: any) => b.agentId === agentId && b.match?.channel === CHANNEL_ID && typeof b.match?.accountId === 'string' && b.match.accountId !== ''
    );
    if (matched) {
      resolvedAccountId = matched.match.accountId;
    }
  }
  const accId = resolvedAccountId || DEFAULT_ACCOUNT_ID;
  const defAccount = cfg?.channels?.[CHANNEL_ID] || {};
  let currAccount = defAccount;
  if (accId === DEFAULT_ACCOUNT_ID) {
    // 如果在 channels.tuitui.accounts 中也配置了默认账号信息，则覆盖 channels.tuitui 中的默认账号信息，保持兼容性（openclaw 有个"自动合并默认账号信息到 accounts" 的 cli 选项）
    // 即：default账号默认走根节点的配置；如果子账号有字段则覆盖
    const subDefInfo = defAccount.accounts?.[DEFAULT_ACCOUNT_ID];
    if (subDefInfo) {
      const { enabled, dmPolicy, groupPolicy, requireMention, emojiReaction, monitorEnabled } = subDefInfo;
      currAccount = {
        enabled: enabled === undefined ? defAccount.enabled : enabled,
        appId: subDefInfo.appId || defAccount.appId,
        appSecret: subDefInfo.appSecret || defAccount.appSecret,
        dmPolicy: dmPolicy === undefined ? defAccount.dmPolicy : dmPolicy,
        allowFrom: mergeArrs(subDefInfo.allowFrom, defAccount.allowFrom),
        groupPolicy: groupPolicy === undefined ? defAccount.groupPolicy : groupPolicy,
        groupAllowFrom: mergeArrs(subDefInfo.groupAllowFrom, defAccount.groupAllowFrom),
        requireMention: requireMention === undefined ? defAccount.requireMention : requireMention,
        emojiReaction: emojiReaction === undefined ? defAccount.emojiReaction : emojiReaction,
        monitorEnabled: monitorEnabled === undefined ? defAccount.monitorEnabled : monitorEnabled,
      };
      //console.log("resolveAccount", accId, accountId, currAccount);
    }
  } else {
    currAccount = currAccount.accounts?.[accId];
  }
  return { accountId: accId, ...getAccountInfo(currAccount) };
};

/**
 * 判断 tuituiAccounts 列表中是否存在至少1个允许私聊的用户（私聊白名单、配对、或者open）
 */
export async function isAllowOneOfAccount(
  tuituiAccounts: string[],
  apiRuntime: any,
  account: { accountId: string; allowFrom: string[]; dmPolicy: string }
): Promise<boolean> {
  if (!tuituiAccounts || tuituiAccounts.length === 0) return false;

  const { accountId, allowFrom, dmPolicy } = account;
  if (dmPolicy === 'open') return true;

  // 1. 检查静态配置的白名单
  if (tuituiAccounts.some(item => allowFrom.includes(item))) return true;

  // 2. 检查动态配对存储的白名单
  try {
    const storeData = await apiRuntime?.channel?.pairing?.readAllowFromStore?.({ channel: CHANNEL_ID, accountId });
    const storeAllowFrom = parseAllowFroms(storeData);
    if (tuituiAccounts.some(item => storeAllowFrom.includes(item))) return true;
  } catch {
    // 忽略读取存储时的错误
  }

  return false;
}

/**
 * 判断单个用户是否允许私聊
 */
export async function isAllowAccount(
  tuituiAccount: string,
  apiRuntime: any,
  account: { accountId: string; allowFrom: string[]; dmPolicy: string }
): Promise<boolean> {
  return isAllowOneOfAccount([tuituiAccount], apiRuntime, account);
}
